import { XIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import classNames from 'classnames'
import React, { useState } from 'react'
import { match } from 'ts-pattern'
import { useIsScrolledToBottom } from '../hooks/use-is-scrolled-to-bottom.tsx'
import { GhostButton } from './buttons/ghost-button.tsx'
import { OutlineButton } from './buttons/outline-button.tsx'
import * as styles from './dropdown-filter.css.ts'
import { Checkbox } from './input/checkbox.tsx'
import { RangeInput } from './input/range-input.tsx'
import { SearchInput } from './input/search-input.tsx'
import { Switch } from './switch.tsx'

type SwitchToggle = {
  type: 'switch'
  id: string
  text: string
  value: boolean
  onToggle: () => unknown
  disabled?: boolean
}

type CheckboxToggle = {
  type: 'checkbox'
  id: string
  text: string
  value: boolean
  onToggle: () => unknown
  icon?: React.ReactNode
  disabled?: boolean
  count?: number
}

export type Filter = SwitchToggle | CheckboxToggle

type ToggleGroup = {
  type: 'group'
  id: string
  text: string
  icon?: React.ReactNode
  filters: Filter[]
  onToggleGroup: () => unknown
  disabled?: boolean
  /**
   * Gives the group a search box with this accessible name, narrowing its own options by their text.
   * For groups whose option list is unbounded — one entry per project, say — where scrolling the
   * panel is not a way to find anything.
   */
  searchLabel?: string
}

type NoLabelToggleGroup = {
  type: 'noLabelGroup'
  id: string
  filters: Filter[]
  disabled?: boolean
}

type Divider = {
  type: 'divider'
  id: string
}

type ScoreInputGroup = {
  type: 'scoreInputGroup'
  id: string
  text: string
  icon?: React.ReactNode
  minScore: number | null
  maxScore: number | null
  min: number
  max: number
  step?: number
  onMinScoreChange: (value: number | null) => unknown
  onMaxScoreChange: (value: number | null) => unknown
  disabled?: boolean
}

export type DropdownElement = ToggleGroup | Divider | NoLabelToggleGroup | ScoreInputGroup

export type DropdownFilterProps = {
  filterElements: DropdownElement[]
  onToggleAllFiltersOff: () => unknown
  isAnyNonDefaultFilterActive: boolean
  label?: string
  contentClassName?: string
  resetIcon?: React.ReactNode
  resetLabel?: string
  disabled?: boolean
  /**
   * Which trigger edge the menu hangs from. Collision avoidance is off, so a right-justified trigger needs
   * `end` or the menu runs off the viewport.
   */
  align?: DropdownMenu.DropdownMenuContentProps['align']
}

export const DropdownFilter: React.FC<DropdownFilterProps> = ({
  filterElements,
  label,
  onToggleAllFiltersOff,
  contentClassName,
  isAnyNonDefaultFilterActive,
  resetIcon,
  resetLabel,
  disabled,
  align,
}) => {
  const selectedGroupCount = calculateSelectedGroupCount(filterElements)
  const { isScrolledToBottom, handleScroll } = useIsScrolledToBottom()

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <OutlineButton
          state={selectedGroupCount > 0 ? 'active' : 'inactive'}
          size="small"
          aria-label={label}
          disabled={disabled}
        >
          Filter ({selectedGroupCount})
        </OutlineButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={classNames(
          styles.dropdownContainer[!isScrolledToBottom ? 'withBottomInnerShadow' : 'base'],
          styles.disableOutlineHover,
          contentClassName
        )}
        onCloseAutoFocus={e => e.preventDefault()}
        align={align}
        avoidCollisions={false}
        sticky="always"
        onScroll={handleScroll}
      >
        {isAnyNonDefaultFilterActive && (
          <div style={{ display: 'flex', justifyContent: 'start' }}>
            <GhostButton size="small" onClick={onToggleAllFiltersOff}>
              {resetIcon ?? <XIcon size={16} weight="light" style={{ color: mappedColors.neutral.halo }} />}
              {resetLabel ?? 'Clear all selected'}
            </GhostButton>
          </div>
        )}
        {filterElements.map(filterElement =>
          match(filterElement)
            .with({ type: 'group' }, group => <ToggleGroupItem key={group.id} group={group} />)
            .with({ type: 'noLabelGroup' }, ({ filters, id }) => {
              return (
                <DropdownMenu.Group key={id}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {filters.map(renderFilter)}
                  </div>
                </DropdownMenu.Group>
              )
            })
            .with({ type: 'divider' }, ({ id }) => (
              <DropdownMenu.Separator key={id} asChild>
                <div className={styles.separator} />
              </DropdownMenu.Separator>
            ))
            .with(
              { type: 'scoreInputGroup' },
              ({
                id,
                text,
                icon,
                minScore,
                maxScore,
                min,
                max,
                step,
                onMinScoreChange,
                onMaxScoreChange,
                disabled,
              }) => {
                const hasActiveFilter = minScore !== null || maxScore !== null

                return (
                  <DropdownMenu.Group
                    key={id}
                    className={styles.group[hasActiveFilter ? 'base' : 'allFiltersInactive']}
                    aria-label={text}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          className={classNames(
                            styles.label[disabled ? 'disabled' : 'default'],
                            icon ? styles.labelWithIcon : undefined
                          )}
                        >
                          {icon}
                          {text}
                        </span>
                      </div>
                      <RangeInput
                        minValue={minScore}
                        maxValue={maxScore}
                        onMinChange={onMinScoreChange}
                        onMaxChange={onMaxScoreChange}
                        min={min}
                        max={max}
                        step={step}
                        label={text}
                        size="small"
                        disabled={disabled}
                      />
                    </div>
                  </DropdownMenu.Group>
                )
              }
            )
            .exhaustive()
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

/**
 * One labelled checkbox group. Its own component so a searchable group can hold the query it filters
 * its options with — the dropdown maps over elements and has nowhere to keep per-group state.
 */
const ToggleGroupItem: React.FC<{ group: ToggleGroup }> = ({ group }) => {
  const { filters, text, icon, onToggleGroup, disabled, searchLabel } = group
  const [search, setSearch] = useState('')
  const query = search.trim().toLowerCase()
  const shownFilters = query === '' ? filters : filters.filter(filter => filter.text.toLowerCase().includes(query))
  const isAllFiltersInactive = filters.every(filter => !filter.value)

  return (
    <DropdownMenu.Group
      className={styles.group[isAllFiltersInactive ? 'allFiltersInactive' : 'base']}
      aria-label={text}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DropdownMenu.CheckboxItem
            className={styles.checkboxItem}
            onSelect={event => event.preventDefault()}
            checked={filters.some(filter => filter.value)}
            onCheckedChange={onToggleGroup}
            aria-label={text}
            disabled={disabled}
          >
            <Checkbox
              checked={filters.some(filter => filter.value)}
              Indicator={DropdownMenu.ItemIndicator}
              icon="dash"
              state={disabled ? 'disabled' : 'default'}
            />
            <span className={classNames(styles.label['default'], icon ? styles.labelWithIcon : undefined)}>
              {icon}
              {text}
            </span>
          </DropdownMenu.CheckboxItem>
        </div>
        {searchLabel && (
          <div style={{ marginLeft: '24px' }} onKeyDown={event => event.stopPropagation()}>
            <SearchInput value={search} onChange={setSearch} size="small" name={searchLabel} />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginLeft: '24px' }}>
          {shownFilters.map(renderFilter)}
          {shownFilters.length === 0 && <span className={styles.checkboxText['disabled']}>No matches</span>}
        </div>
      </div>
    </DropdownMenu.Group>
  )
}

const renderFilter = (filter: SwitchToggle | CheckboxToggle) => {
  return match(filter)
    .with({ type: 'checkbox' }, ({ id, text, value, onToggle, icon, disabled, count }) => (
      <CheckboxItem
        key={id}
        text={text}
        isChecked={() => value}
        onToggleColumnFilter={onToggle}
        icon={icon}
        disabled={disabled}
        count={count}
      />
    ))
    .with({ type: 'switch' }, ({ id, text, value, onToggle, disabled }) => (
      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Switch key={id} checked={value} onCheckedChange={onToggle} aria-label={text} disabled={disabled} />
        <span className={styles.label[disabled && !value ? 'disabled' : 'default']}>{text}</span>
      </div>
    ))
    .exhaustive()
}

type CheckboxItemProps = {
  text: string
  isChecked: () => boolean
  onToggleColumnFilter: () => unknown
  icon?: React.ReactNode
  disabled?: boolean
  count?: number
}

function CheckboxItem({ text, isChecked, onToggleColumnFilter, icon, disabled, count }: CheckboxItemProps) {
  return (
    <DropdownMenu.CheckboxItem
      className={styles.checkboxItem}
      onSelect={event => event.preventDefault()}
      checked={isChecked()}
      onCheckedChange={onToggleColumnFilter}
      disabled={disabled}
    >
      <Checkbox
        checked={isChecked()}
        Indicator={DropdownMenu.ItemIndicator}
        state={disabled ? 'disabled' : 'default'}
      />
      {icon}
      <span className={styles.checkboxText[disabled && !isChecked() ? 'disabled' : 'default']}>{text}</span>
      {count !== undefined && <span className={styles.checkboxCount}>{`(${count})`}</span>}
    </DropdownMenu.CheckboxItem>
  )
}

export const isEitherToggleGroup = (group: DropdownElement) => group.type === 'group' || group.type === 'noLabelGroup'
const isToggleGroup = (group: DropdownElement) => group.type === 'group'
const isNoLabelToggleGroup = (group: DropdownElement) => group.type === 'noLabelGroup'
const isScoreInputGroup = (group: DropdownElement) => group.type === 'scoreInputGroup'

const calculateSelectedGroupCount = (filterElements: DropdownElement[]) => {
  const selectedToggleGroupsCount = filterElements
    .filter(isToggleGroup)
    .filter(group => group.filters.some(filter => filter.value)).length

  const selectedNoLabelToggleGroupsCount = filterElements
    .filter(isNoLabelToggleGroup)
    .flatMap(group => group.filters)
    .filter(filter => filter.value).length

  const selectedScoreInputGroupsCount = filterElements
    .filter(isScoreInputGroup)
    .filter(group => group.minScore !== null || group.maxScore !== null).length

  return selectedToggleGroupsCount + selectedNoLabelToggleGroupsCount + selectedScoreInputGroupsCount
}
